import { mockApiActiveHeader } from "@/mock-api/ids.ts";
import { isMockApiEnabled } from "@/mock-api/msw/mswInit.ts";
import { loadForeverPromise, loadForeverType, mockHeader } from "@/utils/fetchClient.mock.ts";
import { isObjectWithKey } from "@warrenio/utils/isObject";
import type { RequestOptions } from "openapi-fetch";
import z from "zod";

/** MSW catastrophic failure (request bypassed). Due to a Chrome bug, usually. */
export class MockApiFailureError extends Error {
    name = "MockApiFailureError";

    constructor() {
        super("Mock service worker crashed. Please reload the page.");
    }
}

/** MSW handler is missing for a path */
export class MissingMockApiError extends Error {
    name = "MissingMockApiError";

    constructor(public readonly url: string) {
        const urlObj = new URL(url);
        super(`Mock API handler not found for path: "${urlObj.pathname}"`);
    }
}

function isMswUnhandledError(message: string) {
    if (!import.meta.env.DEV) return false;

    return message.includes("[MSW] Cannot bypass a request");
}

let mswCrashed = false;

export const fetchWithMock: typeof fetch = async (...args) => {
    // Prevent sending any further requests if MSW has crashed
    if (!import.meta.env.PROD && isMockApiEnabled() && mswCrashed) {
        throw new MockApiFailureError();
    }

    const response = await fetch(...args);

    if (response.headers.get(mockHeader) === loadForeverType) {
        return await loadForeverPromise();
    }

    if (!import.meta.env.PROD && isMockApiEnabled()) {
        if (response.status === 500 && response.statusText === "Request Handler Error") {
            let error;
            try {
                error = await response.clone().json();
            } catch (e) {
                console.error("Failed to parse MSW error response", e);
            }

            if (isObjectWithKey(error, "message")) {
                const { message } = error;
                if (typeof message === "string" && isMswUnhandledError(message)) {
                    throw new MissingMockApiError(response.url);
                }
            }
        }

        if (!response.headers.has(mockApiActiveHeader)) {
            mswCrashed = true;
            throw new MockApiFailureError();
        }
    }

    return response;
};

export type FormValue = string | number | boolean | null | undefined;
export type FormBody = Record<string, FormValue>;

function valueToFormString(value: FormValue) {
    if (value == null) {
        return "";
    }

    return `${value}`;
}

/** This results in "multipart/form-data" */
export function multiPartFormBodySerializer(body: FormBody) {
    const fd = new FormData();
    for (const [k, v] of Object.entries(body)) {
        if (v === undefined) continue;
        fd.append(k, valueToFormString(v));
    }
    return fd;
}

/** This results in "application/x-www-form-urlencoded" */
export function urlEncodedFormBodySerializer(body: FormBody) {
    const urlParams = new URLSearchParams();
    for (const [k, v] of Object.entries(body)) {
        if (v === undefined) continue;
        urlParams.append(k, valueToFormString(v));
    }
    return urlParams;
}

const formBodyValidator = /*#__PURE__*/ formBodySchema();

// Rollup has issues with #__PURE__ annotations for this expression, so extract it to a function
function formBodySchema() {
    return z.record(z.union([z.string(), z.number(), z.boolean(), z.null(), z.undefined()]));
}

/**
 * Spread this into `apiClient` options for requests expecting a "application/x-www-form-urlencoded" body.
 * @example apiClient.POST("/path", { ...urlEncodedBody, body: { key: "value" } })
 */
export const urlEncodedBody = {
    headers: {
        "Content-Type": "application/x-www-form-urlencoded",
    },
    bodySerializer: (body: unknown) =>
        urlEncodedFormBodySerializer(!import.meta.env.PROD ? formBodyValidator.parse(body) : (body as FormBody)),
} satisfies RequestInit & RequestOptions<unknown>;

export const jsonEncodedBody = {
    headers: {
        "Content-Type": "application/json",
    },
    bodySerializer: (body: unknown) => JSON.stringify(body),
} satisfies RequestInit & RequestOptions<unknown>;
